import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import Leaderboard from './components/Leaderboard';
import Profile from './components/Profile';

function App() {
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState('light');
  const [language, setLanguage] = useState('en');
  const [activeView, setActiveView] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND || 'http://localhost:5000';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }

    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedLang = localStorage.getItem('language') || 'en';
    setTheme(savedTheme);
    setLanguage(savedLang);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const fetchCurrentUser = async (token) => {
    try {
      const res = await fetch(`${backendUrl}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        if (data.user.language) setLanguage(data.user.language);
        if (data.user.theme) {
          setTheme(data.user.theme);
          document.documentElement.classList.toggle('dark', data.user.theme === 'dark');
        }
      } else {
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Fetch user error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('authToken', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setActiveView('dashboard');
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'hi' : 'en';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const t = (key) => {
    const translations = {
      en: {
        appTitle: 'SIH Sports Assessment',
        appSubtitle: 'Democratizing talent assessment with on-device AI',
        dashboard: 'Dashboard',
        leaderboard: 'Leaderboard',
        profile: 'Profile',
        admin: 'Admin',
        logout: 'Logout',
        login: 'Login'
      },
      hi: {
        appTitle: 'SIH खेल मूल्यांकन',
        appSubtitle: 'ऑन-डिवाइस AI के साथ प्रतिभा मूल्यांकन का लोकतंत्रीकरण',
        dashboard: 'डैशबोर्ड',
        leaderboard: 'लीडरबोर्ड',
        profile: 'प्रोफ़ाइल',
        admin: 'एडमिन',
        logout: 'लॉगआउट',
        login: 'लॉगिन'
      }
    };
    return translations[language][key] || key;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login backendUrl={backendUrl} onLogin={handleLogin} language={language} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                AI
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">{t('appTitle')}</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('appSubtitle')}</p>
              </div>
            </div>

            <nav className="flex items-center gap-2">
              <button
                onClick={() => setActiveView('dashboard')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeView === 'dashboard'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {t('dashboard')}
              </button>
              
              <button
                onClick={() => setActiveView('leaderboard')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeView === 'leaderboard'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                🏆 {t('leaderboard')}
              </button>

              {user.role === 'admin' && (
                <button
                  onClick={() => setActiveView('admin')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeView === 'admin'
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  👑 {t('admin')}
                </button>
              )}

              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                title="Toggle theme"
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </button>

              <button
                onClick={toggleLanguage}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                title="Toggle language"
              >
                {language === 'en' ? '🇮🇳 हि' : '🇬🇧 EN'}
              </button>

              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                </button>
                
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <button
                    onClick={() => setActiveView('profile')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                  >
                    {t('profile')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                  >
                    {t('logout')}
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'dashboard' && (
          <Dashboard backendUrl={backendUrl} user={user} language={language} />
        )}
        {activeView === 'leaderboard' && (
          <Leaderboard backendUrl={backendUrl} user={user} language={language} />
        )}
        {activeView === 'profile' && (
          <Profile backendUrl={backendUrl} user={user} setUser={setUser} language={language} />
        )}
        {activeView === 'admin' && user.role === 'admin' && (
          <AdminDashboard backendUrl={backendUrl} language={language} />
        )}
      </main>
    </div>
  );
}

export default App;
